import React, { useCallback } from 'react';
import { Event, EventRegistrationForm, EventRegistrationSelection } from '../generated/api/dsv-public/model';
import { Button, FormControl, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { EventInformation } from '../shared/EventInformation';
import { useIsMobile } from '../hooks/use-is-mobile';

type InitialQuestionnaireProps = {
  event?: Event;
  registrationForm?: EventRegistrationForm;
  handleSubmitInitialQuestionnaire: (registrationSelection: EventRegistrationSelection) => void;
};

export const InitialQuestionnaireForm: React.FC<InitialQuestionnaireProps> = ({
  event,
  registrationForm,
  handleSubmitInitialQuestionnaire,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const handleRegistrationSelection = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      handleSubmitInitialQuestionnaire((event.target as HTMLInputElement).value as EventRegistrationSelection);
    },
    [handleSubmitInitialQuestionnaire]
  );

  return (
    <>
      <EventInformation event={event} registrationForm={registrationForm} />
      <FormControl sx={{ mt: 2, minWidth: 120, width: '100%' }}>
        <h2>{t('forms.initial-questionnaire.title')}</h2>
        <p style={{ textAlign: 'center' }}>{t('forms.initial-questionnaire.instructions')}</p>
        {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.MYSELF) && (
          <Typography textAlign="start" marginTop="10px">
            <b>{t('forms.initial-questionnaire.selection.myself')}:</b>{' '}
            <Trans i18nKey="forms.initial-questionnaire.selection.myself.description">
              Text <strong>bold text</strong> text
            </Trans>
          </Typography>
        )}
        {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.FAMILY) && (
          <Typography textAlign="start" marginTop="10px">
            <b>{t('forms.initial-questionnaire.selection.my-family')}:</b>{' '}
            <Trans i18nKey="forms.initial-questionnaire.selection.my-family.description">
              Text <strong>bold text</strong> text
            </Trans>
          </Typography>
        )}
        {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.FAMILY_MEMBERS) && (
          <Typography textAlign="start" marginTop="10px">
            <b>{t('forms.initial-questionnaire.selection.family-members')}:</b>{' '}
            <Trans i18nKey="forms.initial-questionnaire.selection.family-members.description">
              Text <strong>bold text</strong> text
            </Trans>
          </Typography>
        )}
        {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.ME_AND_OTHERS) && (
          <Typography textAlign="start" marginTop="10px">
            <b>{t('forms.initial-questionnaire.selection.myself-and-others')}:</b>{' '}
            {t('forms.initial-questionnaire.selection.myself-and-others.description')}
          </Typography>
        )}
        {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.LEADER) && (
          <Typography textAlign="start" marginTop="10px">
            <b>{t('forms.initial-questionnaire.selection.leader')}:</b>{' '}
            {t('forms.initial-questionnaire.selection.leader.description')}
          </Typography>
        )}

        <Stack
          flexDirection={isMobile ? 'column' : 'row'}
          flexWrap="wrap"
          gap="15px"
          alignItems="center"
          justifyContent="center"
          marginTop="30px"
        >
          {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.MYSELF) && (
            <Button value={EventRegistrationSelection.MYSELF} onClick={handleRegistrationSelection} variant="outlined">
              {t('forms.initial-questionnaire.selection.myself')}
            </Button>
          )}
          {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.FAMILY) && (
            <Button value={EventRegistrationSelection.FAMILY} onClick={handleRegistrationSelection} variant="outlined">
              {t('forms.initial-questionnaire.selection.my-family')}
            </Button>
          )}
          {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.FAMILY_MEMBERS) && (
            <Button
              value={EventRegistrationSelection.FAMILY_MEMBERS}
              onClick={handleRegistrationSelection}
              variant="outlined"
            >
              {t('forms.initial-questionnaire.selection.family-members')}
            </Button>
          )}
          {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.ME_AND_OTHERS) && (
            <Button
              value={EventRegistrationSelection.ME_AND_OTHERS}
              onClick={handleRegistrationSelection}
              variant="outlined"
            >
              {t('forms.initial-questionnaire.selection.myself-and-others')}
            </Button>
          )}
          {registrationForm?.registeredBySelections?.includes(EventRegistrationSelection.LEADER) && (
            <Button value={EventRegistrationSelection.LEADER} onClick={handleRegistrationSelection} variant="outlined">
              {t('forms.initial-questionnaire.selection.leader')}
            </Button>
          )}
        </Stack>
      </FormControl>
    </>
  );
};
